import { AxiosRequestConfig } from 'axios';
import * as React from 'react';
import {
  IAxiosProps
} from './withAxios';

export default class Component<P={}, S={}, SS=any> extends React.PureComponent<P, S, SS> {
  // Build a typed POSTer given a component axios.
  protected buildPost<TRequest, TResponse>(component: React.Component<IAxiosProps>, uri: string, includeAuth = true) {
    return async (requestData: TRequest) => {
      const config = await this.buildAxiosRequestConfig(includeAuth);
      // tslint:disable-next-line:no-console
      console.log(`posting to ${uri} includeAuth=${includeAuth} with ${JSON.stringify(requestData)}`, component.props.axios.defaults.baseURL);
      const response = await component.props.axios.post(uri, requestData, config);
      // Just trust the server -_-. Can’t do that once API versioning (or just change URI for versioning?).
      return response.data as TResponse;
    };
  }
  // Build a typed PUTter given a component axios.
  protected buildPut<TRequest, TResponse>(component: React.Component<IAxiosProps>, uri: string, includeAuth = true) {
    return async (requestData: TRequest) => {
      const config = await this.buildAxiosRequestConfig(includeAuth);
      // tslint:disable-next-line:no-console
      console.log(`putting to ${uri} includeAuth=${includeAuth} with ${JSON.stringify(requestData)}`, component.props.axios.defaults.baseURL);
      const response = await component.props.axios.put(uri, requestData, config);
      return response.data as TResponse;
    };
  }
  // Build a typed GETer given a component axios.
  protected buildGet<TResult>(component: React.Component<IAxiosProps>, uri: string, includeAuth = true) {
    return async () => {
      const config = await this.buildAxiosRequestConfig(includeAuth);
      // tslint:disable-next-line:no-console
      console.log(`getting ${uri} includeAuth=${includeAuth}`, component.props.axios.defaults.baseURL);
      const response = await component.props.axios.get(uri, config);
      return response.data as TResult;
    };
  }
  // Build a typed GETer given a component axios with Params.
  protected buildGetWithParams<TResult, TParams>(component: React.Component<IAxiosProps>, uri: string, includeAuth = true) {
    return async (requestParams: TParams) => {
      const config = await this.buildAxiosRequestConfig(includeAuth);
      // tslint:disable-next-line:no-console
      console.log(`getting ${uri} includeAuth=${includeAuth} with ${JSON.stringify(requestParams)}`, component.props.axios.defaults.baseURL);
      const response = await component.props.axios.get(uri, Object.assign(config, { params: requestParams }));
      return response.data as TResult;
    };
  }

  // Build a typed DELETEer given a component axios.
  protected buildDelete<TResult>(component: React.Component<IAxiosProps>, uri: string, includeAuth = true) {
    return async () => {
      const config = await this.buildAxiosRequestConfig(includeAuth);
      // tslint:disable-next-line:no-console
      console.log(`deleting ${uri} includeAuth=${includeAuth}`, component.props.axios.defaults.baseURL);
      const response = await component.props.axios.delete(uri, config);
      return response.data as TResult;
    };
  }

  // Build a typed DELETEer given a component axios with Params.
  protected buildDeleteWithParams<TResult, TParams>(component: React.Component<IAxiosProps>, uri: string, includeAuth = true) {
    return async (requestParams: TParams) => {
      const config = await this.buildAxiosRequestConfig(includeAuth);
      // tslint:disable-next-line:no-console
      console.log(`deleting ${uri} includeAuth=${includeAuth} with ${JSON.stringify(requestParams)}`, component.props.axios.defaults.baseURL);
      const response = await component.props.axios.delete(uri, Object.assign(config, { params: requestParams}));
      return response.data as TResult;
    };
  }

  // Cast state to the correct type when using the property pattern instead of the constructor pattern. Call like: readonly state = this.buildState({});
  protected buildState(state: Readonly<S>): Readonly<S> {
    return state;
  }

  // Build an HTMLInputElement event handler which modifies state.
  //
  // You should save this to a property so that you do not rebind functions
  // during each render. For example:
  //
  //     private handleThingChanged = this.stateInputEventHandler(v => ({ thing: v, }));
  protected stateInputEventHandler<K extends keyof S>(pick:(v:string)=>Pick<S, K>) {
    return (event:React.ChangeEvent<HTMLInputElement & {
      // Manual types for when imported by grit-leadership-api
      value: string;
    }>) => {
      this.setState(pick(event.target.value));
    };
  }
  protected stateSelectEventHandler<K extends keyof S>(pick:(v:string[])=>Pick<S, K>) {
    return (event:React.ChangeEvent<HTMLSelectElement & {
      // Manual types for when imported by grit-leadership-api.
      options: {
        length: number;
        selectedIndex: number;
        [index: number]: {
          selected: boolean;
          value: string;
        }
      },
    }>) => {
      this.setState(pick(Array.from(event.target.options).filter(o => o.selected).map(o => o.value)));
    };
  }
  protected stateTextAreaEventHandler<K extends keyof S>(pick:(v:string)=>Pick<S, K>) {
    return (event:React.ChangeEvent<HTMLTextAreaElement & {
      // Manual types for when imported by grit-leadership-api.
      value: string;
    }>) => {
      this.setState(pick(event.target.value));
    };
  }
  protected stateInputEventHandlerAsync<K extends keyof S>(pickAsync:(elem:HTMLInputElement)=>Promise<Pick<S, K>|undefined>) {
    return async (event:React.ChangeEvent<HTMLInputElement>) => {
      const newState = await pickAsync(event.target);
      if (newState) {
        this.setState(newState);
      }
    };
  }

  // Run an asynchronous thing which sets some state first and sets
  // some state after. The after is only ever run if the actionAsync
  // succeeds or it is not cancelled. This assumes that cancellation
  // only happens when the component is unmounted, which may not be
  // the case. doCatch() is run synchronously and the afterState is
  // set after doCatch() returns.
  protected async runCancellableStatefulAsync<K1 extends keyof S, K2 extends keyof S>(component: React.Component<IAxiosProps>, beforeState: Pick<S, K1>, afterState: Pick<S, K2>, actionAsync: () => Promise<void|{}>, doCatch: (ex: any) => void, throwCancel: boolean = false) {
    try {
      this.setState(beforeState);
      await actionAsync();
      if (component.props.axios.defaults.cancelToken !== undefined && component.props.axios.defaults.cancelToken.reason !== undefined) {
        return;
      }
      this.setState(afterState);
    } catch (ex) {
      if (component.props.axios.defaults.cancelToken !== undefined && component.props.axios.defaults.cancelToken.reason !== undefined) {
        // Not allowed to setState() anymore!
        return;
      }
      // Schedule setState to avoid Zalgo. Guarantees that it will run (like try {} finally {}) but
      // also avoids behaving differently when React decides to asynchronously queue setState()
      // versus run it immediately.
      setImmediate(() => this.setState(afterState));
      doCatch(ex);
    }
    if (throwCancel) {
      // TODO Figure out how to throw cancel
      if (component.props.axios.defaults.cancelToken !== undefined) {
        component.props.axios.defaults.cancelToken.throwIfRequested();
      }
    }
  }

  protected async buildAxiosRequestConfig(includeAuth: boolean): Promise<AxiosRequestConfig> {
    return {
    };
  }
}



// WEBPACK FOOTER //
// ./src/components/Component.tsx